'use client';

import React, { useState, useEffect } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PageBackdrop from '@/components/PageBackdrop';
import { SUPABASE_ANON_KEY, CONTACT_EMAIL_ENDPOINT } from '@/lib/supabase/config';

function useScrollReveal() {
  useEffect(() => {
    const elements = document.querySelectorAll('.reveal');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

const serviceOptions = [
  'Recording Engineering',
  'Mixing',
  'Mastering',
  'Mix & Master (Bundle)',
  'Session Drumming (Remote)',
  'Session Drumming (In-Studio)',
  'Post Production / Editing',
  'Songwriting / Co-Write',
  'Full Production Package',
  'Other / Not Sure Yet',
];

const budgetOptions = [
  'Under £500',
  '£500 – £1,000',
  '£1,000 – £2,500',
  '£2,500 – £5,000',
  '£5,000+',
  "I'm flexible, let's discuss",
];

interface FormState {
  name: string;
  email: string;
  band: string;
  service: string;
  budget: string;
  details: string;
}

const initialForm: FormState = {
  name: '',
  email: '',
  band: '',
  service: '',
  budget: '',
  details: '',
};

const inputStyle: React.CSSProperties = {
  background: '#1a1a1a',
  border: '1px solid #ff1a1a',
  color: '#ffffff',
  fontFamily: 'Manrope, sans-serif',
  fontSize: '1rem',
  padding: '10px 14px',
  width: '100%',
  outline: 'none',
  borderRadius: 0,
  transition: 'box-shadow 0.2s ease',
};

const labelStyle: React.CSSProperties = {
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '0.7rem',
  letterSpacing: '0.15em',
  textTransform: 'uppercase',
  color: '#cccccc',
  display: 'block',
  marginBottom: '8px',
};

export default function Contact() {
  useScrollReveal();
  const [form, setForm] = useState<FormState>(initialForm);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      setError('Please enter a valid email address.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(CONTACT_EMAIL_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok || data.error)
        throw new Error(data.error || 'Something went wrong. Please try again.');
      setSubmitted(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{ minHeight: '100vh', background: 'transparent', color: '#ffffff', overflowX: 'hidden' }}
    >
      <PageBackdrop src="/assets/images/diggity-2.jpg" />
      <Header />

      {/* ─── PAGE HEADER ─── */}
      <section style={{ padding: '60px 24px 48px', borderBottom: '1px solid #ff1a1a' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <p
            style={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.7rem',
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: '#ff1a1a',
              marginBottom: '16px',
            }}
          >
            // Contact
          </p>
          <h1
            style={{
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 900,
              textTransform: 'uppercase',
              fontSize: 'clamp(2.5rem, 7vw, 5.5rem)',
              lineHeight: '0.95',
              color: '#ffffff',
              textShadow: '0 0 20px rgba(255,26,26,0.7)',
              marginBottom: '16px',
            }}
          >
            Let&apos;s Build
            <br />
            <span style={{ color: '#ff1a1a' }}>Something Heavy.</span>
          </h1>
          <p
            style={{
              fontFamily: 'Manrope, sans-serif',
              color: '#cccccc',
              fontSize: '1.05rem',
              maxWidth: '500px',
              lineHeight: '1.7',
            }}
          >
            Fill out the form below and you&apos;ll hear back within 24–48 hours.
          </p>
        </div>
      </section>

      {/* ─── FORM + SIDEBAR ─── */}
      <section style={{ padding: '60px 24px 80px' }}>
        <div
          style={{
            maxWidth: '1280px',
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '48px',
          }}
        >
          {/* ─── FORM ─── */}
          <div className="reveal" style={{ gridColumn: 'span 2' }}>
            {submitted ? (
              <div
                style={{
                  background: '#1a1a1a',
                  border: '1px solid #ff1a1a',
                  padding: '60px 40px',
                  textAlign: 'center',
                  minHeight: '400px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <div
                  style={{
                    background: '#ff1a1a',
                    color: '#000000',
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    padding: '6px 16px',
                    marginBottom: '24px',
                    display: 'inline-block',
                  }}
                >
                  Message Sent
                </div>
                <h2
                  style={{
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 900,
                    textTransform: 'uppercase',
                    fontSize: '2rem',
                    color: '#ffffff',
                    textShadow: '0 0 10px rgba(255,26,26,0.5)',
                    marginBottom: '16px',
                  }}
                >
                  Message Sent.
                </h2>
                <p
                  style={{
                    fontFamily: 'Manrope, sans-serif',
                    color: '#cccccc',
                    fontSize: '1rem',
                    maxWidth: '400px',
                    lineHeight: '1.7',
                    marginBottom: '32px',
                  }}
                >
                  Thanks for reaching out. Expect a reply within 24–48 hours. Let&apos;s make
                  something brutal.
                </p>
                <button
                  onClick={() => {
                    setSubmitted(false);
                    setForm(initialForm);
                  }}
                  className="btn-outline"
                  style={{ fontSize: '0.85rem', padding: '10px 28px' }}
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                noValidate
                style={{ background: '#1a1a1a', border: '1px solid #ff1a1a' }}
              >
                <div
                  style={{
                    background: '#ff1a1a',
                    color: '#000000',
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    padding: '8px 16px',
                  }}
                >
                  Booking Enquiry
                </div>
                <div
                  style={{ padding: '28px', display: 'flex', flexDirection: 'column', gap: '20px' }}
                >
                  {/* Name + Email */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '16px',
                    }}
                  >
                    <div>
                      <label style={labelStyle}>
                        Your Name <span style={{ color: '#ff1a1a' }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        required
                        placeholder="First & Last Name"
                        style={inputStyle}
                        onFocus={(e) =>
                          (e.currentTarget.style.boxShadow = '0 0 10px rgba(255,26,26,0.5)')
                        }
                        onBlur={(e) => (e.currentTarget.style.boxShadow = 'none')}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>
                        Email <span style={{ color: '#ff1a1a' }}>*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        required
                        placeholder="your@email.com"
                        style={inputStyle}
                        onFocus={(e) =>
                          (e.currentTarget.style.boxShadow = '0 0 10px rgba(255,26,26,0.5)')
                        }
                        onBlur={(e) => (e.currentTarget.style.boxShadow = 'none')}
                      />
                    </div>
                  </div>

                  {/* Band */}
                  <div>
                    <label style={labelStyle}>Band / Project Name</label>
                    <input
                      type="text"
                      name="band"
                      value={form.band}
                      onChange={handleChange}
                      placeholder="Your band or project name"
                      style={inputStyle}
                      onFocus={(e) =>
                        (e.currentTarget.style.boxShadow = '0 0 10px rgba(255,26,26,0.5)')
                      }
                      onBlur={(e) => (e.currentTarget.style.boxShadow = 'none')}
                    />
                  </div>

                  {/* Service + Budget */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '16px',
                    }}
                  >
                    <div>
                      <label style={labelStyle}>
                        Service Needed <span style={{ color: '#ff1a1a' }}>*</span>
                      </label>
                      <select
                        name="service"
                        value={form.service}
                        onChange={handleChange}
                        required
                        style={{ ...inputStyle, cursor: 'pointer' }}
                      >
                        <option value="" disabled>
                          Select a service...
                        </option>
                        {serviceOptions.map((s) => (
                          <option key={s} value={s} style={{ background: '#1a1a1a' }}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Budget Range</label>
                      <select
                        name="budget"
                        value={form.budget}
                        onChange={handleChange}
                        style={{ ...inputStyle, cursor: 'pointer' }}
                      >
                        <option value="" disabled>
                          Select range...
                        </option>
                        {budgetOptions.map((b) => (
                          <option key={b} value={b} style={{ background: '#1a1a1a' }}>
                            {b}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Details */}
                  <div>
                    <label style={labelStyle}>
                      Project Details <span style={{ color: '#ff1a1a' }}>*</span>
                    </label>
                    <textarea
                      name="details"
                      value={form.details}
                      onChange={handleChange}
                      required
                      rows={6}
                      placeholder="Tell me about your project — genre, number of songs, timeline, any reference tracks, and anything else I should know..."
                      style={{ ...inputStyle, resize: 'none' }}
                      onFocus={(e) =>
                        (e.currentTarget.style.boxShadow = '0 0 10px rgba(255,26,26,0.5)')
                      }
                      onBlur={(e) => (e.currentTarget.style.boxShadow = 'none')}
                    />
                  </div>

                  {/* Submit */}
                  <div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="btn-primary"
                      style={{
                        fontSize: '0.9rem',
                        padding: '14px 40px',
                        opacity: loading ? 0.6 : 1,
                        cursor: loading ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {loading ? 'Sending...' : 'Send It'}
                    </button>
                    {error && (
                      <p
                        style={{
                          fontFamily: '"JetBrains Mono", monospace',
                          fontSize: '0.75rem',
                          color: '#ff1a1a',
                          marginTop: '12px',
                        }}
                      >
                        ⚠ {error}
                      </p>
                    )}
                    <p
                      style={{
                        fontFamily: '"JetBrains Mono", monospace',
                        fontSize: '0.65rem',
                        color: '#444444',
                        marginTop: '12px',
                        letterSpacing: '0.08em',
                      }}
                    >
                      * Required fields. No spam, ever.
                    </p>
                  </div>
                </div>
              </form>
            )}
          </div>

          {/* ─── SIDEBAR ─── */}
          <div className="reveal" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Response time */}
            <div style={{ background: '#1a1a1a', border: '1px solid #ff1a1a' }}>
              <div
                style={{
                  background: '#ff1a1a',
                  color: '#000000',
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  padding: '6px 12px',
                }}
              >
                Response Time
              </div>
              <div style={{ padding: '16px' }}>
                <p
                  style={{
                    fontFamily: 'Manrope, sans-serif',
                    color: '#cccccc',
                    fontSize: '0.95rem',
                    lineHeight: '1.7',
                  }}
                >
                  All inquiries answered personally within{' '}
                  <strong style={{ color: '#ffffff' }}>24–48 hours</strong>.
                </p>
              </div>
            </div>

            {/* What to expect */}
            <div style={{ background: '#1a1a1a', border: '1px solid #ff1a1a' }}>
              <div
                style={{
                  background: '#ff1a1a',
                  color: '#000000',
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  padding: '6px 12px',
                }}
              >
                What to Expect
              </div>
              <ul
                style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}
              >
                {[
                  'Initial consultation call or email exchange',
                  'Clear quote with timeline and deliverables',
                  'Revision policy outlined upfront',
                  'Regular progress updates throughout',
                ].map((item, i) => (
                  <li
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px',
                      fontFamily: 'Manrope, sans-serif',
                      color: '#cccccc',
                      fontSize: '0.95rem',
                      lineHeight: '1.6',
                    }}
                  >
                    <span
                      style={{
                        width: '6px',
                        height: '6px',
                        background: '#ff1a1a',
                        flexShrink: 0,
                        marginTop: '8px',
                      }}
                    />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Social */}
            <div style={{ background: '#1a1a1a', border: '1px solid #ff1a1a' }}>
              <div
                style={{
                  background: '#ff1a1a',
                  color: '#000000',
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  padding: '6px 12px',
                }}
              >
                Find Me Online
              </div>
              <div
                style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}
              >
                {[
                  {
                    label: 'Instagram',
                    handle: '@diggity.glover',
                    href: 'https://www.instagram.com/diggity.glover/',
                  },
                  {
                    label: 'TikTok',
                    handle: '@diggity.glover',
                    href: 'https://www.tiktok.com/@diggity.glover',
                  },
                  {
                    label: 'YouTube',
                    handle: '@Diggity.Glover',
                    href: 'https://www.youtube.com/@Diggity.Glover',
                  },
                  {
                    label: 'Facebook',
                    handle: 'Diggity Glover',
                    href: 'https://www.facebook.com/profile.php?id=100081772446953',
                  },
                ].map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none' }}
                  >
                    <span
                      style={{
                        fontFamily: '"JetBrains Mono", monospace',
                        fontSize: '0.65rem',
                        color: '#666666',
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        display: 'block',
                      }}
                    >
                      {social.label}
                    </span>
                    <span
                      style={{
                        fontFamily: 'Manrope, sans-serif',
                        fontSize: '0.95rem',
                        color: '#ff1a1a',
                      }}
                    >
                      {social.handle}
                    </span>
                  </a>
                ))}
              </div>
            </div>

            {/* Note */}
            <div
              style={{
                background: '#1a1a1a',
                border: '1px solid rgba(255,26,26,0.4)',
                padding: '16px',
              }}
            >
              <p
                style={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.7rem',
                  color: '#ff1a1a',
                  letterSpacing: '0.08em',
                  lineHeight: '1.6',
                }}
              >
                // Currently accepting new clients for Q2 2026. Limited slots available for mixing
                and full production packages.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
